import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import { checkForErrors, isProStudioViewer } from '../../utils';
import {
  Credentials,
  Project,
  ProjectUser,
  Providers,
} from '../prostudio.api-service.types';

export const getProjectData = async (
  ctx: ProStudioContext,
  _event: ProStudioEvents
): Promise<{
  id: string;
  project: Project;
  credentials?: Credentials;
  providers?: Providers;
  projectUsers?: ProjectUser[];
} | void> => {
  const projectId = ctx.project?.id;
  const userId = ctx.userInfo?.id;

  if (!userId) {
    throwErrorWithId('User ID is required');
    return;
  }

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  const role = ctx.userProjects?.find((p) => p.id === projectId)?.role;
  const isViewer = isProStudioViewer(role ? [role] : []);

  const [
    projectResponse,
    providersResponse,
    projectUsersResponse,
    credentialsResponse,
  ] = await Promise.all([
    urqlGql.getProjectById({ id: projectId }),
    urqlGql.getProviders(),
    urqlGql.getProjectUsers({ projectId }),
    !isViewer ? urqlGql.getCredentials({ projectId }) : undefined,
  ]);

  checkForErrors(projectResponse);
  checkForErrors(providersResponse);
  checkForErrors(projectUsersResponse);

  if (credentialsResponse) {
    checkForErrors(credentialsResponse);
  }

  const projectData = projectResponse.data?.admin_?.projectById;

  let credentialsData: Credentials | undefined;

  if (!isViewer) {
    credentialsData = credentialsResponse?.data?.admin_?.credentialsLimited;
  }

  const providersData = providersResponse.data?.admin_?.providers;
  const projectUsers =
    projectUsersResponse.data?.admin_?.projectById?.users?.rows ?? [];

  if (!projectData) {
    throwErrorWithId('Project data is required');
    return;
  }

  const uniqueCustomerId = projectData.projectProStudioSetting?.customerId;

  if (!uniqueCustomerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  const customersResponse = await urqlGql.getCustomers({ projectId });

  const customerId = customersResponse.data?.gesGetCustomers?.find(
    ({ id }) => id === uniqueCustomerId
  )?.name;

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  return {
    id: customerId,
    project: projectData,
    credentials: credentialsData,
    providers: providersData,
    projectUsers: projectUsers as ProjectUser[],
  };
};
